import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { DsButton, DsEmptyState, DsSpinner } from "../../../design-system";
import { displayLabel } from "../../../utils/displayLabel";
import { payrollOpsService } from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { formatInr, formatPayMonthLabel } from "./runPayrollUtils";
import { downloadBlob } from "./statutoryStatementUtils";

const KIND_META = {
  nps: {
    title: "NPS Remittance",
    screenKey: "statutory_filing_nps",
    description:
      "Employer NPS from finalised payroll only. PRAN is mandatory for remittance. Export for CRA / corporate NPS upload. Not part of salary bank credit.",
    amountKeys: ["NPS (Employer)", "Employer NPS"],
  },
  meal: {
    title: "Meal Voucher Disbursement",
    screenKey: "statutory_filing_meal",
    description:
      "Meal voucher (Fixed CTC, non-payroll) from finalised payroll only. Export for vendor / finance load. Not part of salary bank net pay.",
    amountKeys: ["Meal Voucher"],
  },
};

function amountFromRow(row, keys) {
  const other = row?.employerBenefitsOtherByName || {};
  const paid = row?.paidByName || {};
  for (const k of keys) {
    const v = Number(other[k] ?? paid[k] ?? 0);
    if (v > 0) return Math.round(v * 100) / 100;
  }
  const all = { ...paid, ...other };
  for (const [name, amt] of Object.entries(all)) {
    const n = String(name || "").toLowerCase();
    if (keys.some((k) => n === String(k).toLowerCase() || n.includes(String(k).toLowerCase()))) {
      const v = Number(amt) || 0;
      if (v > 0) return Math.round(v * 100) / 100;
    }
  }
  return 0;
}

/**
 * NPS / Meal remittance — finalised payroll months only (same gate as PF / ESI / PT).
 */
export default function BenefitRemittancePanel({ kind = "nps", darkMode = false }) {
  const meta = KIND_META[kind] || KIND_META.nps;
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canExport =
    isAdmin ||
    hasScreenAccess("payroll", meta.screenKey, "export") ||
    hasScreenAccess("payroll", "statutory_filing", "export");

  const [payMonth, setPayMonth] = useState("");
  const [finalizedMonths, setFinalizedMonths] = useState([]);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setMonthsLoading(true);
        const monthsRes = await payrollOpsService.listFinalizedPayrollMonths();
        const list = monthsRes?.data || monthsRes || [];
        const months = (Array.isArray(list) ? list : [])
          .map((m) => ({
            payMonth: String(m.payMonth || "").slice(0, 7),
            id: m.id,
            finalizedAt: m.finalizedAt,
          }))
          .filter((m) => /^\d{4}-\d{2}$/.test(m.payMonth));
        if (!cancelled) {
          setFinalizedMonths(months);
          if (months.length && !payMonth) {
            setPayMonth(months[0].payMonth);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setFinalizedMonths([]);
          setError(err?.response?.data?.message || err.message || "Failed to load finalised months");
        }
      } finally {
        if (!cancelled) setMonthsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial finalised months load only
  }, []);

  const load = useCallback(async () => {
    const ym = String(payMonth || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(ym)) {
      setRows([]);
      setHasLoaded(false);
      return;
    }
    if (!finalizedMonths.some((m) => m.payMonth === ym)) {
      setRows([]);
      setError(
        `${formatPayMonthLabel(ym)} is not finalised. Complete Finalise Payroll, then reload.`
      );
      setHasLoaded(true);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const hit = finalizedMonths.find((m) => m.payMonth === ym);
      let runId = Number(hit?.id);
      if (!runId) {
        const runRes = await payrollOpsService.findRun(ym);
        const run = runRes?.data || runRes?.run || runRes;
        runId = Number(run?.id);
        if (run && !run.finalizedAt) {
          setRows([]);
          setError(
            `${formatPayMonthLabel(ym)} is not finalised. Complete Finalise Payroll, then reload.`
          );
          setHasLoaded(true);
          return;
        }
      }
      if (!runId) {
        setRows([]);
        setError("No finalised payroll run found for this month.");
        setHasLoaded(true);
        return;
      }
      const reg = await payrollOpsService.getSalaryRegister(runId, ym);
      const data = reg?.data || reg;
      const registerRows = data?.rows || [];
      if (!Array.isArray(registerRows) || !registerRows.length) {
        setRows([]);
        setError(
          `No processed employees for ${formatPayMonthLabel(ym)}. Process and finalise payroll, then refresh.`
        );
        setHasLoaded(true);
        return;
      }
      const built = registerRows
        .map((r) => {
          const amount = amountFromRow(r, meta.amountKeys);
          if (amount <= 0) return null;
          const pran = String(r.pran || "").replace(/\D/g, "");
          return {
            employeeId: r.employeeId,
            empCode: r.empCode || "",
            name: r.name || "",
            branch: r.branch || "",
            paidDays: r.paidDays,
            amount,
            pran: pran || "",
            pranMissing: kind === "nps" && !/^\d{12}$/.test(pran),
          };
        })
        .filter(Boolean);
      setRows(built);
      setError("");
      setHasLoaded(true);
    } catch (err) {
      console.error(err);
      setRows([]);
      setError(err?.response?.data?.message || err.message || "Failed to load");
      setHasLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [payMonth, finalizedMonths, meta.amountKeys, kind]);

  useEffect(() => {
    if (monthsLoading) return;
    if (!payMonth) {
      setHasLoaded(false);
      return;
    }
    load();
  }, [load, monthsLoading, payMonth]);

  const totals = useMemo(() => {
    const amount = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const missingPran = rows.filter((r) => r.pranMissing).length;
    return { amount: Math.round(amount * 100) / 100, count: rows.length, missingPran };
  }, [rows]);

  const exportExcel = async () => {
    if (!canExport) {
      toast.error("You do not have export access");
      return;
    }
    if (!rows.length) {
      toast.error("Nothing to export");
      return;
    }
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(meta.title);
    const headers =
      kind === "nps"
        ? ["Emp Code", "Name", "PRAN", "Branch", "Paid days", "NPS (Employer)", "PRAN status"]
        : ["Emp Code", "Name", "Branch", "Paid days", "Meal Voucher"];
    ws.addRow(headers);
    rows.forEach((r) => {
      if (kind === "nps") {
        ws.addRow([
          r.empCode,
          r.name,
          r.pran || "",
          r.branch,
          r.paidDays ?? "",
          r.amount,
          r.pranMissing ? "Missing PRAN" : "OK",
        ]);
      } else {
        ws.addRow([r.empCode, r.name, r.branch, r.paidDays ?? "", r.amount]);
      }
    });
    ws.addRow([]);
    ws.addRow(["Total employees", totals.count]);
    ws.addRow(["Total amount", totals.amount]);
    const buf = await wb.xlsx.writeBuffer();
    downloadBlob(`${kind}-remittance-${payMonth}.xlsx`, new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }));
    toast.success("Exported");
  };

  const cardCls = darkMode
    ? "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
    : "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]";

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className={`${cardCls} shrink-0 p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{meta.title}</h3>
            <p className="mt-1 max-w-3xl text-xs text-[hsl(var(--ds-muted-fg))]">{meta.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-[hsl(var(--ds-muted-fg))]">
              Pay month
              <select
                value={payMonth}
                disabled={monthsLoading || !finalizedMonths.length}
                onChange={(e) => setPayMonth(e.target.value)}
                className="ml-2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1.5 text-sm"
              >
                {!finalizedMonths.length ? (
                  <option value="">No finalised months</option>
                ) : (
                  finalizedMonths.map((m) => (
                    <option key={m.payMonth} value={m.payMonth}>
                      {formatPayMonthLabel(m.payMonth)}
                    </option>
                  ))
                )}
              </select>
            </label>
            <DsButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={load}
              disabled={loading || monthsLoading || !payMonth || !finalizedMonths.length}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </DsButton>
            {canExport ? (
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                onClick={exportExcel}
                disabled={!rows.length}
              >
                <Download size={14} />
                Export Excel
              </DsButton>
            ) : null}
          </div>
        </div>
        {hasLoaded && finalizedMonths.length ? (
          <div className="mt-3 flex flex-wrap gap-4 text-xs">
            <span>
              Employees: <strong>{totals.count}</strong>
            </span>
            <span>
              Total: <strong>{formatInr(totals.amount)}</strong>
            </span>
            {kind === "nps" && totals.missingPran > 0 ? (
              <span className="font-semibold text-amber-700">
                {totals.missingPran} missing PRAN — fix before remittance
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {monthsLoading || loading ? (
        <div className="flex flex-1 items-center justify-center">
          <DsSpinner />
        </div>
      ) : !finalizedMonths.length ? (
        <div className={`${cardCls} flex flex-1 items-center justify-center p-6`}>
          <DsEmptyState
            title="No finalised payroll months"
            description="Finalise a pay month under Run Payroll before filing NPS / Meal voucher remittance (same as PF / ESI)."
          />
        </div>
      ) : !hasLoaded || (!rows.length && !error) ? (
        <div className={`${cardCls} flex flex-1 items-center justify-center p-6`}>
          <DsEmptyState
            title={`No ${kind === "nps" ? "NPS" : "Meal voucher"} amounts`}
            description={`No amounts found for ${formatPayMonthLabel(payMonth)}. Process payroll with the benefit enabled, finalise, then refresh.`}
          />
        </div>
      ) : (
        <div className={`${cardCls} flex min-h-0 flex-1 flex-col overflow-hidden`}>
          {error ? (
            <p className="shrink-0 border-b border-[hsl(var(--ds-border))] bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {error}
            </p>
          ) : null}
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="ds-table w-full min-w-[640px] text-xs">
              <thead className="sticky top-0 z-10 bg-[hsl(var(--ds-muted))]">
                <tr>
                  <th className="px-2 py-2 text-left">Emp Code</th>
                  <th className="px-2 py-2 text-left">Name</th>
                  {kind === "nps" ? <th className="px-2 py-2 text-left">PRAN</th> : null}
                  <th className="px-2 py-2 text-left">Branch</th>
                  <th className="px-2 py-2 text-right">Paid days</th>
                  <th className="px-2 py-2 text-right">
                    {kind === "nps" ? "NPS (Employer)" : "Meal Voucher"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employeeId || r.empCode} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-2 py-1.5">{r.empCode}</td>
                    <td className="px-2 py-1.5">{r.name}</td>
                    {kind === "nps" ? (
                      <td
                        className={`px-2 py-1.5 tabular-nums ${
                          r.pranMissing ? "font-semibold text-amber-700" : ""
                        }`}
                      >
                        {r.pran || "— Missing"}
                      </td>
                    ) : null}
                    <td className="px-2 py-1.5">{displayLabel(r.branch)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.paidDays ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                      {formatInr(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
